import type { TranslationKeyPath, TranslationNode } from './key-paths.ts';
import type { SupportedLanguage } from './languages.ts';
import enGbAccount from './locales/en-GB/account.json' with { type: 'json' };
import enGbAndeLineRequests from './locales/en-GB/ande-line-requests.json' with { type: 'json' };
import enGbCommon from './locales/en-GB/common.json' with { type: 'json' };
import enGbHome from './locales/en-GB/home.json' with { type: 'json' };
import enGbInvites from './locales/en-GB/invites.json' with { type: 'json' };
import enGbReservations from './locales/en-GB/reservations.json' with { type: 'json' };
import enGbSupport from './locales/en-GB/support.json' with { type: 'json' };
import enGbValidation from './locales/en-GB/validation.json' with { type: 'json' };
import enGbVenueAgent from './locales/en-GB/venue-agent.json' with { type: 'json' };
import enGbVenuePortal from './locales/en-GB/venue-portal.json' with { type: 'json' };
import enGbVenues from './locales/en-GB/venues.json' with { type: 'json' };
import enAccount from './locales/en/account.json' with { type: 'json' };
import enAndeLineRequests from './locales/en/ande-line-requests.json' with { type: 'json' };
import enCommon from './locales/en/common.json' with { type: 'json' };
import enHome from './locales/en/home.json' with { type: 'json' };
import enInvites from './locales/en/invites.json' with { type: 'json' };
import enReservations from './locales/en/reservations.json' with { type: 'json' };
import enSupport from './locales/en/support.json' with { type: 'json' };
import enValidation from './locales/en/validation.json' with { type: 'json' };
import enVenueAgent from './locales/en/venue-agent.json' with { type: 'json' };
import enVenuePortal from './locales/en/venue-portal.json' with { type: 'json' };
import enVenues from './locales/en/venues.json' with { type: 'json' };

/**
 * The single registry of translation namespaces.
 *
 * This is the only file to edit when adding a domain. i18next `resources`, the
 * typed `t()` key space, the namespace list, and the Zod validation key union
 * are all derived from it, so there is no second place to forget.
 *
 * See README.md for the full procedure.
 */
export const catalog = {
  account: { en: enAccount, 'en-GB': enGbAccount },
  common: { en: enCommon, 'en-GB': enGbCommon },
  validation: { en: enValidation, 'en-GB': enGbValidation },
  support: { en: enSupport, 'en-GB': enGbSupport },
  invites: { en: enInvites, 'en-GB': enGbInvites },
  'ande-line-requests': { en: enAndeLineRequests, 'en-GB': enGbAndeLineRequests },
  home: { en: enHome, 'en-GB': enGbHome },
  venues: { en: enVenues, 'en-GB': enGbVenues },
  reservations: { en: enReservations, 'en-GB': enGbReservations },
  'venue-portal': { en: enVenuePortal, 'en-GB': enGbVenuePortal },
  'venue-agent': { en: enVenueAgent, 'en-GB': enGbVenueAgent },
  // `satisfies` fails the build if a namespace omits a supported language, which
  // is the mistake that would otherwise surface as a runtime lookup miss.
} as const satisfies Record<string, Record<SupportedLanguage, TranslationNode>>;

export type Catalog = typeof catalog;

/** Namespace names, e.g. `'common' | 'support'`. */
export type TranslationNamespace = keyof Catalog;

/**
 * English is the source of truth, so it defines the typed key space. Target
 * locales are filled by Lingo.dev and fall back to English per key at runtime.
 */
export type NamespaceTranslations = {
  [Namespace in TranslationNamespace]: Catalog[Namespace]['en'];
};

/** The namespace that unprefixed keys resolve against. */
export const defaultNS = 'common';

/** A namespaced key string accepted by `t('ns:path.to.key')`. */
export type NamespacedTranslationKey<Ns extends TranslationNamespace> =
  `${Ns}:${TranslationKeyPath<NamespaceTranslations[Ns]>}`;
